import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, ShoppingCart, Plus, Check, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const RFQGenerator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState(projectId || '');
  const [sourceItems, setSourceItems] = useState({ boq_items: [], vo_items: [] });
  const [selected, setSelected] = useState(new Set());
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState('boq');

  useEffect(() => {
    axios.get(`${BACKEND}/api/projects?page=1&limit=200`, { withCredentials: true })
      .then(r => setProjects((r.data.projects || r.data).filter(p => p.status === 'Confirmed'))).catch(() => {});
    axios.get(`${BACKEND}/api/vendors`, { withCredentials: true })
      .then(r => setVendors(r.data)).catch(() => {});
  }, []);

  const fetchItems = useCallback(async () => {
    if (!filterProject) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND}/api/projects/${filterProject}/rfq-source-items`, { withCredentials: true });
      setSourceItems(data);
      setSelected(new Set());
    } catch { toast.error('Failed to load items'); }
    finally { setLoading(false); }
  }, [filterProject]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const items = tab === 'boq' ? sourceItems.boq_items : sourceItems.vo_items;

  const toggleItem = (idx) => {
    setSelected(s => { const n = new Set(s); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  };
  const selectAll = () => setSelected(new Set(items.map((_, i) => i)));
  const clearAll = () => setSelected(new Set());

  const handleGenerate = async () => {
    if (selected.size === 0) { toast.error('Select at least one item'); return; }
    setGenerating(true);
    try {
      const selectedItems = [...selected].map(i => items[i]);
      const { data } = await axios.post(`${BACKEND}/api/projects/${filterProject}/generate-rfq`, {
        items: selectedItems, vendor_ids: selectedVendors, remarks,
      }, { withCredentials: true });
      toast.success(data.message);
      setSelected(new Set());
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate RFQ'); }
    finally { setGenerating(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate(-1)} variant="outline" className="border-zinc-300 rounded-none mb-3" data-testid="rfq-back">
            <ArrowLeft size={20} className="mr-2" /> Back
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900" data-testid="rfq-title">Generate RFQ from BOQ / VO</h1>
          <p className="text-sm text-zinc-600 mt-1">Select items to create indents (quotation requests) for procurement</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
            className="bg-white border border-zinc-200 rounded-none p-2.5 text-sm min-w-[260px]" data-testid="rfq-project-filter">
            <option value="">Select Project</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.pi_number} — {p.client_name}</option>)}
          </select>
        </div>

        {filterProject && (
          <>
            {/* Source Tabs */}
            <div className="flex border-b border-zinc-200 mb-4">
              {[{ key: 'boq', label: `BOQ Items (${sourceItems.boq_items.length})` }, { key: 'vo', label: `VO Items (${sourceItems.vo_items.length})` }].map(t => (
                <button key={t.key} onClick={() => { setTab(t.key); setSelected(new Set()); }}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 ${tab === t.key ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500'}`}
                  data-testid={`rfq-tab-${t.key}`}>{t.label}</button>
              ))}
            </div>

            {/* Action Bar */}
            <div className="flex items-center gap-3 mb-3">
              <Button onClick={selectAll} variant="outline" size="sm" className="rounded-none text-xs" data-testid="rfq-select-all">Select All</Button>
              <Button onClick={clearAll} variant="outline" size="sm" className="rounded-none text-xs">Clear</Button>
              <span className="text-xs text-zinc-500">{selected.size} item(s) selected</span>
            </div>

            {/* Items Table */}
            {loading ? <div className="text-center py-8 text-zinc-500">Loading...</div> : items.length === 0 ? (
              <div className="bg-white border border-zinc-200 p-8 text-center text-sm text-zinc-500">No {tab === 'boq' ? 'BOQ' : 'VO'} items found</div>
            ) : (
              <div className="bg-white border border-zinc-200 overflow-x-auto mb-5">
                <table className="w-full text-sm" data-testid="rfq-items-table">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      {['', 'Description', 'Trade', 'Qty', 'Unit', 'Rate', 'Source'].map(h => (
                        <th key={h} className="px-3 py-2.5 text-left text-[10px] font-bold uppercase text-zinc-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx} className={`border-b border-zinc-100 hover:bg-zinc-50 ${selected.has(idx) ? 'bg-blue-50/50' : ''}`}>
                        <td className="px-3 py-2.5"><input type="checkbox" checked={selected.has(idx)} onChange={() => toggleItem(idx)} data-testid={`rfq-check-${idx}`} /></td>
                        <td className="px-3 py-2.5 text-xs font-medium text-zinc-800 max-w-[250px] truncate">{item.description}</td>
                        <td className="px-3 py-2.5 text-xs text-zinc-600">{item.trade_name}</td>
                        <td className="px-3 py-2.5 text-xs font-bold text-zinc-800 text-right">{(item.quantity || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2.5 text-xs text-zinc-500">{item.unit}</td>
                        <td className="px-3 py-2.5 text-xs text-zinc-600 text-right">Rs {(item.rate || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2.5 text-xs">
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold ${tab === 'boq' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{item.source_ref || item.source_type}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Generate Section */}
            {selected.size > 0 && (
              <div className="bg-white border border-zinc-200 p-4 space-y-3" data-testid="rfq-generate-section">
                <div>
                  <label className="text-xs text-zinc-500 font-bold uppercase">Remarks (optional)</label>
                  <input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="e.g. Urgent requirement"
                    className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="rfq-remarks" />
                </div>
                <Button onClick={handleGenerate} disabled={generating} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="rfq-generate-btn">
                  <ShoppingCart size={18} className="mr-1.5" /> {generating ? 'Generating...' : `Generate ${selected.size} Indent(s)`}
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default RFQGenerator;
